import React, { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { useOfflineAudio } from "../../hooks/useOfflineAudio";
import { API, logListen } from "./practiceApi";

const NIGHT_COUNT_S = 12 * 60;
const FADE_S = 180;
const WIND = [[0, "Full 3h"], [2, "2h"], [1, "1h"]];

// Player for practice listens: day counts at ~60% played; night shows "Played tonight" after ~12 minutes.
export const PracticePlayer = ({ src, kind, title, onLogged }) => {
  const url = src?.startsWith("/api") ? `${API}${src}` : src;
  const offline = useOfflineAudio(kind === "night" ? url : null);
  const ref = useRef(null);
  const played = useRef(0);
  const logged = useRef(false);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { played.current = 0; logged.current = false; }, [url]);
  const [hours, setHours] = useState(0);
  const endAt = useRef(null);
  const arm = (h) => { const a = ref.current; endAt.current = h && a ? a.currentTime + h * 3600 : null; if (a) a.volume = 1; };
  const windDown = () => {
    const a = ref.current;
    if (!a || endAt.current == null || a.paused) return;
    const left = endAt.current - a.currentTime;
    if (left <= 0) { a.pause(); a.volume = 1; endAt.current = null; return; }
    if (left < FADE_S) a.volume = Math.max(0, left / FADE_S);
  };
  const tick = () => {
    windDown();
    const a = ref.current;
    if (!a || logged.current || a.paused) return;
    played.current += 1;
    const enough = kind === "night" ? played.current >= NIGHT_COUNT_S : a.duration && played.current >= a.duration * 0.6;
    if (enough) { logged.current = true; logListen(kind === "night" ? "night" : "day").then((p) => p && onLogged?.(p)); }
  };
  useEffect(() => { const t = setInterval(tick, 1000); return () => clearInterval(t); }); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = () => { const a = ref.current; if (!a) return; if (a.paused) a.play().catch(() => {}); else a.pause(); };
  return (
    <div className="flex items-center gap-3" data-testid={`practice-player-${kind}`}>
      <button type="button" onClick={toggle} aria-label={playing ? "Pause" : "Play"} data-testid={`practice-play-${kind}`}
        className="flex h-11 w-11 items-center justify-center rounded-full border border-[#d7bb7a]/60 text-[#f3ecd9] transition-colors hover:bg-[#d7bb7a]/20">
        {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-[#f3ecd9]">{title}</p>
        {kind === "night" && offline.supported && (
          <button type="button" onClick={offline.state === "saved" ? offline.remove : offline.save} disabled={offline.state === "saving"}
            className="text-[0.65rem] uppercase tracking-[0.2em] text-[#b9ad8f] hover:text-[#f3ecd9]" data-testid="practice-night-offline-btn">
            {offline.state === "saving" ? "Saving to phone…" : offline.state === "saved" ? "Saved · plays offline" : offline.state === "error" ? "Couldn't save · retry" : "Save for offline"}
          </button>
        )}
        {kind === "night" && (
          <div className="mt-1.5 flex items-center gap-1 text-[0.65rem] text-[#b9ad8f]" data-testid="night-wind-down">
            <span className="mr-1">Fade out after</span>
            {WIND.map(([h, label]) => (
              <button key={h} type="button" onClick={() => { setHours(h); if (!ref.current?.paused) arm(h); }} data-testid={`night-wind-down-${h || "full"}`}
                className={`rounded-full border px-2 py-0.5 transition-colors ${hours === h ? "border-[#d7bb7a] bg-[#d7bb7a]/15 text-[#f3ecd9]" : "border-[#2b3d5e] hover:text-[#f3ecd9]"}`}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      <audio ref={ref} src={offline.blobUrl || url} preload="none" onPlay={() => { setPlaying(true); arm(hours); }} onPause={() => setPlaying(false)} onTimeUpdate={windDown} onEnded={() => setPlaying(false)} />
    </div>
  );
};
